import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { workOrderService } from "../services/svc_workorders";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";
import { OrderStatusUpdate } from "../types/combinedTypes";

export const useTSQWorkOrderListView = (
  params: ListRequestSchemaV2,
  source: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.workorder.listv2(params, source),
    queryFn: () => workOrderService.listv2(params),
    enabled: enabled,
  });
};

export const useTSQWorkOrderList = (params: ListRequest) => {
  return useQuery({
    queryKey: queryKeys.workorder.filteredList(params),
    queryFn: () => workOrderService.list(params),
    placeholderData: keepPreviousData,
  });
};

export const useTSQWorkOrderPalletList = (
  source: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.workorder.listv2(null, source),
    queryFn: () => workOrderService.listPallets(),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQWorkOrder = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.workorder.detail(id),
    queryFn: () => workOrderService.getById(id),
    enabled: enabled,
  });
};

export const useTSMCreateWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: workOrderService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create WO:", error);
    },
  });
};

export const useTSMUpdateWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      workOrderService.update(id, data),
    onSuccess: (updateWorkaseOrder) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.detail(updateWorkaseOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update WO:", error);
    },
  });
};

export const useTSMStatusUpdateWO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: OrderStatusUpdate }) =>
      workOrderService.statusUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.all,
      });
    },
    onError: (error) => {
      console.error("Failed to Update WO Status:", error);
    },
  });
};

export const useTSMCompleteWO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: workOrderService.markComplete,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
    },
    onError: (error) => {
      console.error("Failed to mark WO Complete:", error);
    },
  });
};

export const useTSMVoidWO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: workOrderService.markVoid,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
    },
    onError: (error) => {
      console.error("Failed to mark WO Void:", error);
    },
  });
};

export const useTSMBuildWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      workOrderService.build(id, data),
    onSuccess: (builtWorkOrder) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.detail(builtWorkOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to build WO:", error);
    },
  });
};

export const useTSMDeactivateWO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: workOrderService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
    },
    onError: (error) => {
      console.error("Failed to deactivate WO:", error);
    },
  });
};

export const useTSMReactivateWO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: workOrderService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workorder.filteredLists(),
      });
    },
    onError: (error) => {
      console.error("Failed to reactivate WO:", error);
    },
  });
};
